import { LoginCredentials, ApiResponse, AdminUser } from '../types'
import { getDb } from '../../db/index.js'
import { adminUsers, eq } from '../../db/schema/index.js'
import bcrypt from 'bcryptjs'

export const authService = {
  async login(credentials: LoginCredentials): Promise<ApiResponse<{ token: string; user: AdminUser }>> {
    try {
      const db = getDb()

      // Find user by username
      const result = await db.select().from(adminUsers).where(eq(adminUsers.username, credentials.username)).limit(1)
      const user = result[0]

      if (!user) {
        return {
          success: false,
          message: 'Invalid username or password',
        }
      }

      // Verify password
      const isValidPassword = await bcrypt.compare(credentials.password, user.password_hash)

      if (!isValidPassword) {
        return {
          success: false,
          message: 'Invalid username or password',
        }
      }

      // Generate a simple token (in production, use JWT)
      const token = `token-${user.id}-${Date.now()}`

      return {
        success: true,
        data: {
          token,
          user: {
            id: user.id,
            username: user.username,
            created_at: user.created_at,
          }
        },
        message: 'Login successful'
      }
    } catch (error: any) {
      console.error('Database error:', error)
      return {
        success: false,
        message: 'Login failed',
      }
    }
  },

  async logout(): Promise<ApiResponse> {
    // Mock logout for development
    localStorage.removeItem('ntr_auth_token')
    localStorage.removeItem('ntr_user_data')
    return {
      success: true,
      message: 'Logged out successfully'
    }
  },

  async validateToken(token?: string): Promise<ApiResponse<{ user: AdminUser }>> {
    try {
      // If no token provided, try to get from localStorage
      const authToken = token || localStorage.getItem('ntr_auth_token')

      if (!authToken) {
        return {
          success: false,
          message: 'No token provided',
        }
      }

      // Extract user ID from token (simple validation)
      const tokenParts = authToken.split('-')
      if (tokenParts.length !== 3 || tokenParts[0] !== 'token') {
        return {
          success: false,
          message: 'Invalid token format',
        }
      }

      const userId = parseInt(tokenParts[1])
      if (isNaN(userId)) {
        return {
          success: false,
          message: 'Invalid token',
        }
      }

      const db = getDb()
      const result = await db.select().from(adminUsers).where(eq(adminUsers.id, userId)).limit(1)
      const user = result[0]

      if (!user) {
        return {
          success: false,
          message: 'User not found',
        }
      }

      return {
        success: true,
        data: {
          user: {
            id: user.id,
            username: user.username,
            created_at: user.created_at,
          }
        },
        message: 'Token is valid'
      }
    } catch (error: any) {
      console.error('Database error:', error)
      return {
        success: false,
        message: 'Token validation failed',
      }
    }
  },

  async getCurrentUser(): Promise<ApiResponse<AdminUser>> {
    try {
      const token = localStorage.getItem('ntr_auth_token')

      if (!token) {
        return {
          success: false,
          message: 'No authentication token found',
        }
      }

      // Use the validateToken method to get user data
      const validationResult = await this.validateToken(token)

      if (!validationResult.success) {
        return {
          success: false,
          message: validationResult.message || 'Failed to get user data',
        }
      }

      return {
        success: true,
        data: validationResult.data!.user,
        message: 'User data retrieved'
      }
    } catch (error: any) {
      console.error('Database error:', error)
      return {
        success: false,
        message: 'Failed to get user data',
      }
    }
  },
}
